/**
 * Typed client for the Cloud Worker.
 *
 * Everything goes through `request`, which unwraps the `{ error: { code,
 * message } }` envelope and throws an ApiError carrying the machine-readable
 * code — the same contract website-main uses, so error handling reads the same
 * in both codebases.
 *
 * Every field named `…Enc` or `…Wrapped` below is ciphertext. Nothing in this
 * file decrypts anything; that is `vault.ts` and `nodes.ts`, which hold the
 * keys. Keeping the two apart is deliberate — it makes it obvious at a glance
 * that this layer could be logged wholesale without disclosing anything.
 */

import type { KdfParams } from './crypto/argon2';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
    /** The Worker's id for this request, echoed in every error envelope and
     *  logged beside the stack trace. Carried so the UI can show it: a bare
     *  "Something went wrong" is unactionable, whereas the same banner with an
     *  id is one `wrangler tail` grep from the exception that caused it. */
    readonly requestId?: string
  ) {
    super(message);
  }

  /** True for the generic 500 — an unhandled exception in the Worker rather
   *  than a refusal it meant to make. Worth distinguishing: these are our bugs,
   *  and they are the only ones worth showing an id for. */
  get isInternal(): boolean {
    return this.status >= 500 || this.code === 'internal';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      // The session is an HttpOnly cookie set by mauvely.com for the whole
      // domain, so it rides along on same-origin requests here.
      credentials: 'same-origin',
      ...init,
      headers: {
        ...(init.body instanceof ArrayBuffer || init.body instanceof Uint8Array
          ? { 'content-type': 'application/octet-stream' }
          : { 'content-type': 'application/json' }),
        ...(init.headers ?? {})
      }
    });
  } catch {
    throw new ApiError(0, 'network', 'Could not reach the server. Check your connection.');
  }

  if (res.status === 204) return undefined as T;

  const payload = await res.json().catch(() => null);

  if (!res.ok) {
    const envelope = payload as { error?: Record<string, unknown>; requestId?: string } | null;
    const err = envelope?.error;
    throw new ApiError(
      res.status,
      (err?.code as string) ?? 'unknown',
      (err?.message as string) ?? 'Something went wrong.',
      err ?? {},
      // `requestId` sits beside `error` in the envelope, not inside it — see
      // errorResponse() in worker/lib/http.ts.
      typeof envelope?.requestId === 'string' ? envelope.requestId : undefined
    );
  }

  return payload as T;
}

/**
 * The sentence to put in front of someone, given a thrown error.
 *
 * Used by every page's catch block so a failure reads the same wherever it
 * happens, and so an unhandled Worker exception carries its request id instead
 * of being indistinguishable from a refusal we meant to make.
 */
export function errorText(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  if (!err.isInternal) return err.message;
  return err.requestId
    ? `${err.message} If it keeps happening, quote reference ${err.requestId}.`
    : err.message;
}

/** The query string naming a conversation. One helper, because a read and a
 *  write that disagree would silently post into a different channel. */
function channelQuery(project?: string | null, channel?: string | null): string {
  const q = new URLSearchParams();
  if (project) q.set('project', project);
  if (channel) q.set('channel', channel);
  const qs = q.toString();
  return qs ? `?${qs}` : '';
}

const post = <T>(path: string, body?: unknown, init: RequestInit = {}) =>
  request<T>(path, { ...init, method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const del = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'DELETE', body: body === undefined ? undefined : JSON.stringify(body) });

/** Raw bytes, for chunk fetches. Never parsed as JSON. */
async function bytes(path: string, init: RequestInit = {}): Promise<ArrayBuffer> {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    const err = (payload as { error?: Record<string, unknown> })?.error;
    throw new ApiError(res.status, (err?.code as string) ?? 'unknown', (err?.message as string) ?? 'Could not fetch that.');
  }
  return res.arrayBuffer();
}

// ── wire types ──────────────────────────────────────────────────────────────

import type { Branding } from '@shared/branding';

export interface CloudConfig {
  storage: boolean;
  email: boolean;
  bytesPerMb: number;
  maxFileBytes: number;
  /** Where an organisation is administered. Empty when this deployment has no
   *  enterprise Worker, and the admin page then names no link rather than one
   *  it guessed. */
  enterpriseUrl: string;
}

export interface StorageState {
  usedBytes: number;
  limitBytes: number;
  limitMb: number;
  availableBytes: number;
  percent: number;
}

export interface MeResponse {
  user: {
    id: string;
    email: string;
    name: string;
    /** The account's handle, or null. */
    handle: string | null;
    /** True when `email` is derived rather than a mailbox. Render
     *  `displayIdentity` of this user, never `email` on its own. */
    emailIsPlaceholder: boolean;
    /** The account's photo, served by website-main, or null. */
    avatarUrl: string | null;
  };
  /**
   * The largest single file this account may upload, or null for no limit.
   *
   * Per caller, because an organisation sets its own and a plan constant cannot
   * know about one. Prefer this over `config.maxFileBytes`, which is a sanity
   * bound on the API and not anybody's limit.
   */
  maxFileBytes: number | null;
  hasVault: boolean;
  recoveryConfirmed: boolean;
  rootNodeId: string | null;
  publicKeyFingerprint: string | null;
  storage: StorageState | null;
  /** Normalised: an app-only subscription reads as 'free' here exactly as it
   *  does in every server-side gate. Lets the UI show the upgrade panel without
   *  provoking a 403 to find out. */
  plan: string;
  /**
   * The organisation this account is in, or null — which is almost every
   * account. Carried so the chrome can name the company without a second
   * request, and so the admin page knows whose it is.
   */
  organisation: { id: string; slug: string | null; role: string } | null;
  /** What the member's organisation shows instead of Mauvely, or null — which
   *  is almost every account, and is not an absence worth explaining. */
  branding: Branding | null;
}

export interface VaultBlobsWire {
  kekSalt: string;
  kdfParams: KdfParams;
  masterKeyWrappedPassword: string;
  masterKeyWrappedRecovery: string;
  publicKey: string;
  publicKeyFingerprint: string;
  privateKeyWrapped: string;
  rootNodeId: string;
  recoveryVersion: number;
  recoveryConfirmed: boolean;
}

/**
 * A desktop app holding its own key.
 *
 * `state: 'pending'` means it has registered a public key and is waiting for
 * someone to approve it in a browser. The app's public key travels in the
 * page's URL fragment rather than through the server, which is what makes a
 * substituted key detectable — see migration 0026 — so the fingerprint here is
 * no longer a comparison the user has to make; `scope` is what the approval
 * screen shows instead, since it says what approving actually hands over.
 */
export interface DeviceWire {
  id: string;
  publicKey: string;
  fingerprint: string;
  client: string | null;
  label: string | null;
  /** The master key ('vault'), or one app's own folder ('app'). What
   *  `keyWrapped` opens once this device is approved. */
  scope: 'vault' | 'app';
  state: 'pending' | 'active';
  /** The node this device's key opens, for an app-scoped one that has been
   *  given its folder. Null until it has. */
  rootNodeId: string | null;
  createdAt: number;
  approvedAt: number | null;
  lastUsedAt: number | null;
}

/** What `authorise` needs from the URL fragment the app opened this page with. */
export interface AppLinkRequest {
  /** SPKI base64 of the app's ECDH public key. Never sent to the server by the
   *  app — it arrives in the fragment, which is the whole point. */
  publicKey: string;
  client: string;
  label: string | null;
}

export interface PasskeyWire {
  id: string;
  credentialId: string;
  prfSalt: string;
  masterKeyWrapped: string;
  label: string | null;
  createdAt: number;
  lastUsedAt: number | null;
}

export type Role = 'owner' | 'editor' | 'commenter' | 'viewer';

/** A node as the server holds it. Names and types are still ciphertext. */
export interface NodeWire {
  id: string;
  parentId: string | null;
  /** Set on every node in a workspace. The client needs it because a workspace
   *  root's key is not wrapped under the master key — seeing it is what says
   *  "fetch the member-wrapped key before walking this chain". */
  workspaceId: string | null;
  kind: 'folder' | 'file';
  nodeKeyWrapped: string;
  nameEnc: string;
  mimeEnc: string | null;
  contentKeyWrapped: string | null;
  sizeBytes: number;
  plainSizeBytes: number;
  hasThumbnail: boolean;
  currentRevisionId: string | null;
  trashedAt: number | null;
  createdAt: number;
  updatedAt: number;
  role: Role;
}

export interface RevisionWire {
  id: string;
  chunkCount: number;
  chunkSize: number;
  nonceBase: string;
  sizeBytes: number;
}

export interface ShareWire {
  id: string;
  email: string;
  role: Role;
  state: 'pending' | 'accepted' | 'revoked';
  createdAt: number;
  fingerprint: string | null;
  /** The recipient's key differs from the one pinned at share time. Usually a
   *  vault reset; also exactly what a key substitution looks like. */
  keyChanged: boolean;
}

export interface LinkWire {
  id: string;
  role: Role;
  hasPassword: boolean;
  expiresAt: number | null;
  maxDownloads: number | null;
  downloadCount: number;
  viewCount: number;
  createdAt: number;
  lastAccessedAt: number | null;
}

/**
 * The admin dashboard's whole payload.
 *
 * Note what is **not** here: no workspace names, no file names, no message
 * bodies. Those are encrypted to their members and the server holds no key, so
 * an admin sees shape and size and nothing else. See worker/routes/organisations.ts.
 *
 * Nor are seats or SSO, any more. Those belong to the organisation itself,
 * which lives in Mauvely Enterprise now; what is left here is the storage pool,
 * the workspaces drawn against it, and which hostname the console holds.
 */
export interface OrgOverviewWire {
  organisation: {
    id: string;
    name: string;
    slug: string;
    storage: { poolMb: number; allocatedMb: number; unallocatedMb: number };
    /** What the enterprise console holds for this organisation. `state` is
     *  `'none'` when nothing has been claimed. Claiming, proving and certifying
     *  happen there. */
    customDomain: { hostname: string | null; state: string };
  } | null;
  eligible?: boolean;
  admin: boolean;
  members: {
    userId: string;
    /** Null when the account has been deleted and the Cloud row outlived it. */
    email: string | null;
    name: string | null;
    role: string;
    usedBytes: number;
    limitMb: number;
  }[];
  workspaces: {
    id: string;
    ownerUserId: string;
    usedBytes: number;
    members: number;
    files: number;
    createdAt: number;
  }[];
}

export interface EmbedWire {
  id: string;
  nodeId: string;
  slug: string;
  title: string;
  mime: string;
  sizeBytes: number;
  /** `preparing` | `live` | `revoked`. A revoked row stays so the person can
   *  see that an address they shared no longer works. */
  state: string;
  createdAt: number;
  revokedAt: number | null;
}

export interface CourseWire {
  id: string;
  title: string;
  slug: string;
  state: 'live' | 'unpublished';
  access: 'open' | 'named' | 'restricted';
  fileCount: number;
  sizeBytes: number;
  learners: number;
  completions: number;
  publishedAt: number;
  updatedAt: number;
}

export interface CourseLearnerWire {
  id: string;
  name: string | null;
  email: string | null;
  percent: number;
  score: number | null;
  completedAt: number | null;
  seconds: number;
  firstSeenAt: number;
  lastSeenAt: number;
}

export interface CourseAnalyticsWire {
  course: { id: string; title: string; slug: string; state: string };
  summary: {
    learners: number;
    completions: number;
    averageScore: number | null;
    averageSeconds: number | null;
  };
  learners: CourseLearnerWire[];
}

export interface AuditEntryWire {
  id: string;
  userId: string | null;
  nodeId: string | null;
  action: string;
  meta: Record<string, unknown> | null;
  ip: string | null;
  createdAt: number;
}

export interface SharedWithMeWire {
  id: string;
  nodeId: string;
  state: 'pending' | 'accepted';
  role: Role;
  nodeKeyWrapped: string | null;
  kind: 'folder' | 'file';
  nameEnc: string;
  mimeEnc: string | null;
  contentKeyWrapped: string | null;
  sizeBytes: number;
  plainSizeBytes: number;
  hasThumbnail: boolean;
  updatedAt: number;
  sharedAt: number;
  owner: { name: string; email: string | null };
}

export type WorkspaceRole = 'owner' | 'editor' | 'commenter' | 'viewer';

/**
 * One notification, as the server holds it.
 *
 * A *fact*, not a sentence. The wording is composed client-side, because a
 * workspace's name and a file's name are encrypted — the server could not write
 * "Ayse added you to Marketing" even if it wanted to, so it sends the kind, the
 * actor and the ids and lets the reader's browser fill in what it can decrypt.
 */
export interface NotificationWire {
  id: string;
  kind:
    | 'workspace_invite'
    | 'workspace_mention'
    | 'share_invite'
    | 'key_ready'
    | 'review_comment'
    | 'review_reply'
    | 'review_mention'
    | 'review_approved'
    // Pushed in by Mauvely Learn, which keeps its own list. A learner has a
    // bell here whether or not they open Learn today.
    | 'lms_assigned'
    | 'lms_due_soon'
    | 'lms_overdue'
    | string;
  actor: {
    /** Null when the actor has no Mauvely account — a Review reviewer. */
    id: string | null;
    name: string | null;
    email: string | null;
    /** False when the name was typed into a share link rather than
     *  authenticated. The bell must say so; see `describe()`. */
    verified?: boolean;
  } | null;
  workspaceId: string | null;
  nodeId: string | null;
  /** Opaque reference into another product. `<projectId>/<threadId>` for
   *  Review; the URL is built client-side. */
  ref?: string | null;
  /** A plaintext subject, only where the value is one the server could read
   *  anyway. See migration 0012. */
  subject?: string | null;
  readAt: number | null;
  createdAt: number;
}

/**
 * A project, as listed inside a workspace.
 *
 * `nodeKeyWrapped` is null for a workspace member who is not assigned. Since
 * `nameEnc` is encrypted under that key, such a project renders as "a project"
 * and nothing more — which is the truthful amount, and deliberately not hidden:
 * a member should be able to see one exists and ask to be added.
 */
export interface ProjectWire {
  id: string;
  rootNodeId: string;
  nameEnc: string;
  nodeKeyWrapped: string | null;
  role: string | null;
  memberCount: number;
  unread: number;
  createdAt: number;
}

export interface ProjectMemberWire {
  id: string;
  userId: string | null;
  email: string;
  role: string;
  state: 'pending' | 'active' | 'removed';
  /** Assigned, but nobody has been able to wrap the key to them yet — they have
   *  no vault. "Assigned" is not the same as "able to read". */
  awaitingKey: boolean;
}

export interface WorkspaceSummaryWire {
  id: string;
  rootNodeId: string;
  /** The root node's name, encrypted under the root node's own key — so the
   *  member-wrapped key that opens the workspace also reads its name. */
  nameEnc: string;
  nodeKeyWrapped: string | null;
  role: WorkspaceRole;
  isOwner: boolean;
  memberCount: number;
  unread: number;
  /** Usage only — a workspace draws on its owner's pool and has no limit of
   *  its own. See resolveQuotaOwner() in the Worker. */
  storage: { usedBytes: number };
  createdAt: number;
}

export interface WorkspaceMemberWire {
  id: string;
  userId: string | null;
  email: string;
  name: string;
  role: WorkspaceRole;
  state: 'pending' | 'active' | 'removed';
  /** Invited before they had a vault, so there was no public key to wrap to.
   *  An owner's client finishes it on their next unlock. */
  awaitingKey: boolean;
  createdAt: number;
}

export interface WorkspaceDetailWire {
  id: string;
  rootNodeId: string;
  nameEnc: string | null;
  nodeKeyWrapped: string | null;
  role: WorkspaceRole;
  isOwner: boolean;
  lastReadAt: number;
  /** What this workspace is using. */
  storage: StorageState;
  /** The pool it is charged against — its owner's drive. */
  pool: StorageState;
  members: WorkspaceMemberWire[];
}

export interface MessageWire {
  id: string;
  authorId: string;
  authorName: string;
  bodyEnc: string;
  /** Plaintext user ids, deliberately — see crypto/workspace.ts. */
  mentions: string[];
  createdAt: number;
  editedAt: number | null;
}

export interface LinkResolveWire {
  needsPassword: boolean;
  passwordSalt?: string | null;
  kdfParams?: KdfParams | null;
  role?: Role;
  nodeKeyWrapped?: string;
  node?: {
    id: string;
    kind: 'folder' | 'file';
    nameEnc: string;
    mimeEnc: string | null;
    contentKeyWrapped: string | null;
    sizeBytes: number;
    plainSizeBytes: number;
    hasThumbnail: boolean;
  };
  revision?: RevisionWire | null;
  /** Whether the link has a download cap. Only on an answer past the password. */
  capped?: boolean;
}

// ── endpoints ───────────────────────────────────────────────────────────────


/** One machine that syncs into this account. See migration 0024. */
export interface ComputerWire {
  id: string;
  /** The plaintext label — what the approval screen showed beside a
   *  fingerprint, and the name a person set. Not the folder's name, which is
   *  encrypted. */
  label: string | null;
  client: string | null;
  state: string;
  createdAt: number;
  lastUsedAt: number | null;
  /** Null until the machine has actually had somewhere to sync into. A
   *  registered device is a credential; a computer is a credential with a root. */
  rootNodeId: string | null;
  nameEnc: string | null;
  nodeKeyWrapped: string | null;
  updatedAt: number | null;
  files: number;
  bytes: number;
}

export const api = {
  config: () => request<CloudConfig>('/api/cloud/config'),
  me: () => request<MeResponse>('/api/cloud/me'),
  storage: () => request<StorageState>('/api/cloud/storage'),

  vault: {
    get: () => request<VaultBlobsWire>('/api/cloud/vault'),
    setup: (body: Record<string, unknown>) =>
      post<{ ok: true; rootNodeId: string; storageLimitMb: number }>(
        '/api/cloud/vault/setup',
        body
      ),
    confirmRecovery: () => post<{ ok: true }>('/api/cloud/vault/recovery/confirm'),
    /** Someone else's public key, so a node key can be wrapped to them.
     *  404 means they have no vault — the caller falls back to a pending invite. */
    publicKeyFor: (email: string) =>
      request<{ userId: string; email: string; publicKey: string; fingerprint: string }>(
        `/api/cloud/keys/${encodeURIComponent(email)}`
      ),

    /**
     * Passkey wraps of the master key — a third peer of the password and the
     * recovery phrase. The server verifies nothing about these; see
     * worker/routes/passkeys.ts for why that is deliberate.
     */
    passkeys: {
      list: () => request<{ passkeys: PasskeyWire[] }>('/api/cloud/vault/passkeys'),
      enrol: (body: {
        credentialId: string;
        prfSalt: string;
        masterKeyWrapped: string;
        label?: string;
      }) => post<{ ok: true }>('/api/cloud/vault/passkeys', body),
      remove: (id: string) => del<{ ok: true }>(`/api/cloud/vault/passkeys/${id}`),
      touch: (id: string) => post<{ ok: true }>(`/api/cloud/vault/passkeys/${id}/used`)
    },

    /** Desktop apps that hold their own key. See migrations 0008 and 0026. */
    devices: {
      list: () => request<{ devices: DeviceWire[] }>('/api/cloud/vault/devices'),

      /**
       * Register a device on its behalf.
       *
       * The browser does this, not the app, and the ordering forces it: an app
       * has no session until the device-link flow finishes, so at the moment the
       * user is here to approve, the row cannot exist yet. The public key comes
       * from the URL fragment the app opened this page with.
       *
       * Idempotent on the fingerprint, so the app calling this later with its
       * own session finds the row this made rather than queuing a second one.
       */
      register: (body: {
        publicKey: string;
        fingerprint: string;
        client: string;
        label?: string;
        scope: 'vault' | 'app';
      }) => post<{ ok: true; id: string; state: string }>('/api/cloud/vault/devices', body),

      /** The device's own area, and for an app-scoped device its whole vault. */
      createRoot: (id: string, body: { nodeKeyWrapped: string; nameEnc: string }) =>
        post<{ ok: true; rootNodeId: string }>(
          `/api/cloud/vault/devices/${id}/root`,
          body
        ),

      approve: (id: string, keyWrapped: string) =>
        post<{ ok: true }>(`/api/cloud/vault/devices/${id}/approve`, { keyWrapped }),

      revoke: (id: string) =>
        del<{ ok: true; note: string }>(`/api/cloud/vault/devices/${id}`)
    }
  },

  nodes: {
    list: (parentId: string) =>
      request<{ parent: NodeWire; ancestors: NodeWire[]; nodes: NodeWire[] }>(
        `/api/cloud/nodes?parent=${encodeURIComponent(parentId)}`
      ),
    get: (id: string) =>
      request<{ node: NodeWire; ancestors: NodeWire[]; grantedVia: string | null }>(
        `/api/cloud/nodes/${id}`
      ),
    createFolder: (body: {
      parentId: string;
      nameEnc: string;
      nameHmac: string;
      nodeKeyWrapped: string;
    }) => post<{ node: NodeWire }>('/api/cloud/nodes', body),
    update: (
      id: string,
      body: { nameEnc: string; nameHmac: string; parentId?: string; nodeKeyWrapped?: string }
    ) => patch<{ ok: true }>(`/api/cloud/nodes/${id}`, body),
    trash: (id: string) =>
      del<{ ok: true; trashed: number; purgesAfterDays: number }>(`/api/cloud/nodes/${id}`),
    restore: (id: string) => post<{ ok: true; restored: number }>(`/api/cloud/nodes/${id}/restore`),
    purge: (id: string) =>
      del<{ ok: true; freedBytes: number }>(`/api/cloud/nodes/${id}/permanent`),
    /** Kept versions of a file, newest first. */
    revisions: (id: string) =>
      request<{
        kept: number;
        plan: string;
        revisions: {
          id: string;
          sizeBytes: number;
          plainSizeBytes: number;
          label: string | null;
          createdAt: number;
          current: boolean;
        }[];
      }>(`/api/cloud/nodes/${id}/revisions`),
    restoreRevision: (id: string, revisionId: string) =>
      post<{ ok: true; node: NodeWire }>(`/api/cloud/nodes/${id}/revisions/${revisionId}/restore`),
    deleteRevision: (id: string, revisionId: string) =>
      del<{ ok: true }>(`/api/cloud/nodes/${id}/revisions/${revisionId}`),
    // `revisionId` reads an older version. It has to be threaded through both
    // calls together: each revision has its own nonce base, so a manifest and a
    // chunk from different revisions will not decrypt against each other.
    manifest: (id: string, revisionId?: string) =>
      request<{ node: NodeWire; revision: RevisionWire }>(
        `/api/cloud/nodes/${id}/manifest${revisionId ? `?revision=${encodeURIComponent(revisionId)}` : ''}`
      ),
    chunk: (id: string, index: number, revisionId?: string) =>
      bytes(
        `/api/cloud/nodes/${id}/chunks/${index}${revisionId ? `?revision=${encodeURIComponent(revisionId)}` : ''}`
      ),
    putThumbnail: (id: string, data: ArrayBuffer) =>
      request<{ ok: true }>(`/api/cloud/nodes/${id}/thumbnail`, { method: 'POST', body: data }),
    thumbnail: (id: string) => bytes(`/api/cloud/nodes/${id}/thumbnail`)
  },

  computers: {
    /** Outside /vault/ on purpose — it is the one device route a sync client
     *  reaches with an API token, and it carries no key material a token could
     *  use. */
    list: () => request<{ computers: ComputerWire[] }>('/api/cloud/computers')
  },

  trash: {
    list: () => request<{ nodes: NodeWire[]; retentionDays: number }>('/api/cloud/trash'),
    empty: () => del<{ ok: true; freedBytes: number }>('/api/cloud/trash')
  },

  uploads: {
    start: (body: Record<string, unknown>) =>
      post<{
        uploadId: string;
        nodeId: string;
        revisionId: string;
        chunkCount: number;
        expiresAt: number;
      }>('/api/cloud/uploads', body),
    part: (uploadId: string, index: number, data: Uint8Array) =>
      request<{ ok: true }>(`/api/cloud/uploads/${uploadId}/parts/${index}`, {
        method: 'PUT',
        // A fresh copy: the caller's buffer may be a view into a larger one,
        // and fetch would send the whole backing store.
        body: data.slice().buffer as ArrayBuffer,
        headers: { 'content-type': 'application/octet-stream' }
      }),
    complete: (uploadId: string) =>
      post<{ ok: true; node: NodeWire }>(`/api/cloud/uploads/${uploadId}/complete`),
    abort: (uploadId: string) => del<{ ok: true }>(`/api/cloud/uploads/${uploadId}`)
  },

  shares: {
    forNode: (nodeId: string) => request<{ shares: ShareWire[] }>(`/api/cloud/nodes/${nodeId}/shares`),
    invite: (
      nodeId: string,
      body: { email: string; role: Role; nodeKeyWrapped?: string; recipientFingerprint?: string }
    ) => post<{ ok: true; pending: boolean }>(`/api/cloud/nodes/${nodeId}/shares`, body),
    setRole: (shareId: string, role: Role) =>
      patch<{ ok: true }>(`/api/cloud/shares/${shareId}`, { role }),
    revoke: (shareId: string) => del<{ ok: true }>(`/api/cloud/shares/${shareId}`),
    withMe: () => request<{ shares: SharedWithMeWire[] }>('/api/cloud/shared/with-me'),
    byMe: () => request<{ shares: Record<string, unknown>[] }>('/api/cloud/shared/by-me'),
    accept: (shareId: string) =>
      post<{ ok: true; needsKey: boolean }>(`/api/cloud/shares/${shareId}/accept`),
    decline: (shareId: string) => post<{ ok: true }>(`/api/cloud/shares/${shareId}/decline`),
    /** Invites whose recipient has since created a vault, so the owner's client
     *  can wrap the key to them. Only the owner can do that wrap. */
    pendingKeys: () =>
      request<{
        pending: {
          shareId: string;
          nodeId: string;
          email: string;
          role: Role;
          publicKey: string;
          fingerprint: string;
        }[];
      }>('/api/cloud/shared/pending-keys')
  },

  projects: {
    list: (workspaceId: string) =>
      request<{ projects: ProjectWire[] }>(
        `/api/cloud/workspaces/${workspaceId}/projects`
      ),
    create: (workspaceId: string, body: { nameEnc: string; nodeKeyWrapped: string }) =>
      post<{ ok: true; id: string; rootNodeId: string }>(
        `/api/cloud/workspaces/${workspaceId}/projects`,
        body
      ),
    members: (projectId: string) =>
      request<{ members: ProjectMemberWire[] }>(`/api/cloud/projects/${projectId}/members`),
    assign: (
      projectId: string,
      body: { email: string; role: string; nodeKeyWrapped?: string; fingerprint?: string }
    ) => post<{ ok: true; pending: boolean }>(`/api/cloud/projects/${projectId}/members`, body),
    unassign: (projectId: string, memberId: string) =>
      del<{ ok: true; note: string }>(
        `/api/cloud/projects/${projectId}/members/${memberId}`
      ),
    /** Trashes the project's files and drops the row. Members go with it. */
    remove: (projectId: string) => del<{ ok: true }>(`/api/cloud/projects/${projectId}`),
    pendingKeys: () =>
      request<{
        pending: {
          memberId: string; projectId: string; email: string;
          publicKey: string; fingerprint: string; myKeyWrapped: string;
        }[];
      }>('/api/cloud/projects/pending-keys')
  },

  org: {
    overview: () => request<OrgOverviewWire>('/api/cloud/org'),
    addMember: (email: string, storageMb: number) =>
      post<{ ok: true }>('/api/cloud/org/members', { email, storageMb }),
    updateMember: (userId: string, body: { role?: string; storageMb?: number }) =>
      patch<{ ok: true }>(`/api/cloud/org/members/${userId}`, body),
    removeMember: (userId: string) => del<{ ok: true }>(`/api/cloud/org/members/${userId}`)
    // No domain calls. Claiming a hostname, proving it and reading the
    // certificate are the enterprise console's screen; the admin page here
    // prints what `overview` already carries and links across.
  },

  courses: {
    list: () =>
      request<{ courses: CourseWire[]; limit: number; live: number }>('/api/cloud/courses'),
    create: (body: { title: string; sourceNodeId?: string; workspaceId?: string }) =>
      post<{ ok: true; id: string; slug: string }>('/api/cloud/courses', body),
    /** Raw bytes. These are already-compressed assets, so base64 would cost a
     *  third of the transfer for nothing. */
    putFile: (id: string, path: string, bytes: Uint8Array) =>
      request<{ ok: true }>(
        `/api/cloud/courses/${id}/files?path=${encodeURIComponent(path)}`,
        {
          method: 'PUT',
          body: bytes as unknown as BodyInit,
          headers: { 'content-type': 'application/octet-stream' }
        }
      ),
    publish: (id: string) => post<{ ok: true; slug: string }>(`/api/cloud/courses/${id}/publish`),
    unpublish: (id: string) => post<{ ok: true }>(`/api/cloud/courses/${id}/unpublish`),
    update: (id: string, body: { title?: string; access?: string }) =>
      patch<{ ok: true }>(`/api/cloud/courses/${id}`, body),
    setAllowlist: (id: string, emails: string[]) =>
      request<{ ok: true; count: number }>(`/api/cloud/courses/${id}/allowlist`, {
        method: 'PUT',
        body: JSON.stringify({ emails })
      }),
    remove: (id: string) => del<{ ok: true }>(`/api/cloud/courses/${id}`),
    analytics: (id: string) => request<CourseAnalyticsWire>(`/api/cloud/courses/${id}/analytics`)
  },

  embeds: {
    list: () => request<{ embeds: EmbedWire[] }>('/api/cloud/embeds'),
    create: (body: { nodeId: string; title: string; mime: string }) =>
      post<{ ok: true; id: string; slug: string }>('/api/cloud/embeds', body),
    /** Raw bytes: the decrypted copy, which is almost always already-compressed
     *  media. Base64 would cost a third of the transfer for nothing. */
    putContent: (id: string, bytes: Uint8Array) =>
      request<{ ok: true; sizeBytes: number }>(`/api/cloud/embeds/${id}/content`, {
        method: 'PUT',
        body: bytes as unknown as BodyInit,
        headers: { 'content-type': 'application/octet-stream' }
      }),
    publish: (id: string) => post<{ ok: true; slug: string }>(`/api/cloud/embeds/${id}/publish`),
    /** Deletes the copy and stops the address. It cannot reach what an unfurler
     *  has already cached — see docs/SECURITY.md. */
    remove: (id: string) => del<{ ok: true }>(`/api/cloud/embeds/${id}`)
  },

  audit: {
    /** Keyset pagination: feed `nextCursor` back as `before`. */
    list: (opts: { before?: number; limit?: number; action?: string } = {}) => {
      const q = new URLSearchParams();
      if (opts.before) q.set('before', String(opts.before));
      if (opts.limit) q.set('limit', String(opts.limit));
      if (opts.action) q.set('action', opts.action);
      const qs = q.toString();
      return request<{
        entries: AuditEntryWire[];
        nextCursor: number | null;
        scope: 'self' | 'organisation';
      }>(`/api/cloud/audit${qs ? `?${qs}` : ''}`);
    }
  },

  notifications: {
    list: () =>
      request<{ unread: number; notifications: NotificationWire[] }>(
        '/api/cloud/notifications'
      ),
    /** No `id` marks everything read, which is what the panel's header does. */
    read: (id?: string) =>
      post<{ ok: true }>('/api/cloud/notifications/read', id ? { id } : {})
  },

  workspaces: {
    list: () => request<{ workspaces: WorkspaceSummaryWire[] }>('/api/cloud/workspaces'),
    create: (body: { nameEnc: string; nodeKeyWrapped: string }) =>
      post<{ ok: true; id: string; rootNodeId: string }>(
        '/api/cloud/workspaces',
        body
      ),
    get: (id: string) => request<WorkspaceDetailWire>(`/api/cloud/workspaces/${id}`),
    remove: (id: string) => del<{ ok: true; freedBytes: number }>(`/api/cloud/workspaces/${id}`),

    invite: (
      id: string,
      body: { email: string; role: WorkspaceRole; nodeKeyWrapped?: string; fingerprint?: string }
    ) => post<{ ok: true; pending: boolean }>(`/api/cloud/workspaces/${id}/members`, body),
    removeMember: (id: string, memberId: string) =>
      del<{ ok: true }>(`/api/cloud/workspaces/${id}/members/${memberId}`),

    /**
     * `after` is a message id, not a timestamp: ids sort by creation, and a
     * timestamp cursor would skip anything landing in the same second.
     *
     * `project` picks the channel. Omitted is the workspace-wide one, which is
     * also every message written before projects existed.
     */
    messages: (
      id: string,
      after?: string,
      project?: string | null,
      channel?: string | null
    ) => {
      const q = new URLSearchParams();
      if (after) q.set('after', after);
      if (project) q.set('project', project);
      if (channel) q.set('channel', channel);
      const qs = q.toString();
      return request<{ messages: MessageWire[] }>(
        `/api/cloud/workspaces/${id}/messages${qs ? `?${qs}` : ''}`
      );
    },
    channels: {
      list: (id: string) =>
        request<{ channels: { id: string; nameEnc: string; createdAt: number }[] }>(
          `/api/cloud/workspaces/${id}/channels`
        ),
      create: (id: string, nameEnc: string) =>
        post<{ ok: true; id: string }>(`/api/cloud/workspaces/${id}/channels`, { nameEnc }),
      remove: (id: string, channelId: string) =>
        del<{ ok: true }>(`/api/cloud/workspaces/${id}/channels/${channelId}`)
    },

    send: (
      id: string,
      body: { bodyEnc: string; mentions: string[] },
      project?: string | null,
      channel?: string | null
    ) =>
      post<{ ok: true; id: string; createdAt: number }>(
        `/api/cloud/workspaces/${id}/messages${channelQuery(project, channel)}`,
        body
      ),
    edit: (id: string, messageId: string, body: { bodyEnc: string; mentions: string[] }) =>
      patch<{ ok: true }>(`/api/cloud/workspaces/${id}/messages/${messageId}`, body),
    deleteMessage: (id: string, messageId: string) =>
      del<{ ok: true }>(`/api/cloud/workspaces/${id}/messages/${messageId}`),
    markRead: (id: string, project?: string | null) =>
      post<{ ok: true }>(
        `/api/cloud/workspaces/${id}/read${project ? `?project=${encodeURIComponent(project)}` : ''}`
      ),

    /** Invites whose recipient has since created a vault, so an owner's client
     *  can wrap the workspace key to them. Only a member can do that wrap. */
    pendingKeys: () =>
      request<{
        pending: {
          memberId: string;
          workspaceId: string;
          email: string;
          role: WorkspaceRole;
          publicKey: string;
          fingerprint: string;
          myKeyWrapped: string;
        }[];
      }>('/api/cloud/workspaces/pending-keys')
  },

  /**
   * Mauvely Review.
   *
   * One call, and it deliberately returns a *ticket* rather than doing the
   * work. The course bytes are ciphertext at our Worker — it cannot decrypt
   * what it hosts — so the browser has to upload them itself, and this is how
   * it gets permission to without carrying the session to another origin.
   * See worker/routes/review.ts and web/src/review.ts.
   */
  review: {
    handoff: (input: {
      sourceNodeId: string;
      title: string;
      lessonCount: number;
      blockCount: number;
      /** Reuse an existing review: the upload becomes its next round. */
      existingProjectId?: string;
    }) =>
      post<{
        projectId: string;
        slug?: string;
        round?: number;
        token: string;
        expiresAt: number;
        reviewOrigin: string;
      }>('/api/cloud/review/handoff', input),
    /** The live review this node already has, or an empty object. */
    forNode: (nodeId: string) =>
      request<{ projectId?: string; round?: number; title?: string }>(
        `/api/cloud/review/for-node?nodeId=${encodeURIComponent(nodeId)}`
      )
  },

  links: {
    forNode: (nodeId: string) => request<{ links: LinkWire[] }>(`/api/cloud/nodes/${nodeId}/links`),
    create: (nodeId: string, body: Record<string, unknown>) =>
      post<{ ok: true; id: string }>(`/api/cloud/nodes/${nodeId}/links`, body),
    update: (linkId: string, body: Record<string, unknown>) =>
      patch<{ ok: true }>(`/api/cloud/links/${linkId}`, body),
    revoke: (linkId: string) => del<{ ok: true }>(`/api/cloud/links/${linkId}`),
    revokeAll: () => del<{ ok: true; revoked: number }>('/api/cloud/links'),
    mine: () =>
      request<{
        links: (LinkWire & {
          nodeId: string;
          kind: 'folder' | 'file';
          nameEnc: string;
          mimeEnc: string | null;
        })[];
        stats: { active: number; withPassword: number; opens: number; expiringSoon: number };
      }>('/api/cloud/links'),

    /**
     * The public half. No session — the token proves possession of the link.
     *
     * The token goes in the body rather than the query string, and the secret
     * it came from never leaves the fragment at all. `init` is for the bridge,
     * which sends `credentials: 'omit'` and a header of its own.
     */
    resolve: (token: string, passwordProof?: string, init?: RequestInit) =>
      post<LinkResolveWire>('/api/cloud/public/links/resolve', { token, passwordProof }, init),
    chunk: (token: string, index: number, passwordProof?: string, init?: RequestInit) =>
      bytes(`/api/cloud/public/links/chunks/${index}`, {
        ...init,
        headers: {
          ...(init?.headers ?? {}),
          'x-link-token': token,
          ...(passwordProof ? { 'x-link-password': passwordProof } : {})
        }
      })
  }
};
